import { create } from 'zustand';

// Shared by the initial state, `reset`, and `readHexagonConfig`'s fallback for
// a collection with no saved config — one definition, so they can't drift.
export const DEFAULT_HEXAGON_COLOR = '#14B8A6';

/** Default hexagon aggregation resolution, in m² per cell. */
export const DEFAULT_HEXAGON_RESOLUTION = 1000;

/**
 * Zustand store for Collection Results UI state.
 *
 * Design decisions (Zustand v5 best practices):
 *
 * 1. **Object maps instead of Sets** — `expandedNodes` and `checkedNodes` use
 *    `{ [nodeId]: true }` rather than `Set`. This enables per-node selectors
 *    that return a primitive boolean, allowing Zustand's default `Object.is`
 *    equality check to skip re-renders when other nodes change.
 *
 * 2. **Granular selectors** — Components subscribe to `s => s.checkedNodes[nodeId]`
 *    (a boolean) rather than the entire map, so only the affected node re-renders.
 *
 * 3. **Stable action references** — Actions are defined at store creation and
 *    never change identity, making them safe to omit from dep arrays.
 *
 * 4. **No middleware overhead** — State is flat enough that immer is unnecessary.
 *    Object spread handles all updates.
 */
export const useCollectionStore = create((set, get) => ({
	// --- Expanded/collapsed state (object map: { nodeId: true }) ---
	expandedNodes: {},

	expandAll: nodeIds =>
		set(() => {
			const next = {};
			for (const id of nodeIds) next[id] = true;
			return { expandedNodes: next };
		}),

	collapseAll: () => set({ expandedNodes: {} }),

	toggleExpanded: nodeId =>
		set(state => {
			const next = { ...state.expandedNodes };
			if (next[nodeId]) {
				delete next[nodeId];
			} else {
				next[nodeId] = true;
			}
			return { expandedNodes: next };
		}),

	// --- Checked/visible state (object map: { nodeId: true }) ---
	checkedNodes: {},

	checkAll: nodeIds =>
		set(() => {
			const next = {};
			for (const id of nodeIds) next[id] = true;
			return { checkedNodes: next };
		}),

	uncheckAll: () => set({ checkedNodes: {} }),

	toggleChecked: nodeId =>
		set(state => {
			const next = { ...state.checkedNodes };
			if (next[nodeId]) {
				delete next[nodeId];
			} else {
				next[nodeId] = true;
			}
			return { checkedNodes: next };
		}),

	// --- Filter sidebar state ---
	activeFilterCount: 0,
	setActiveFilterCount: count => set({ activeFilterCount: count }),

	// --- Selected object for per-object filter panel ---
	selectedObjectId: null,
	setSelectedObjectId: objectId => set({ selectedObjectId: objectId }),

	// --- Filtered features (client-side derived, consumed by the map layer) ---
	filteredFeatures: [],

	// Per-node_uuid shown count, derived alongside filteredFeatures so panels and
	// tree nodes read an O(1) lookup instead of re-scanning the array each render.
	filteredCountByKey: {},

	setFilteredFeatures: features => {
		// Skip the update when the resulting set is identical to the current one —
		// same length and same feature references in the same order. filterFeatures
		// preserves object references (no cloning) and order (Array.filter), so a
		// shallow element-wise compare reliably detects "nothing changed" and avoids
		// a redundant map reconcile and re-render of every subscriber.
		const prev = get().filteredFeatures;
		if (prev.length === features.length && prev.every((f, i) => f === features[i])) {
			return;
		}

		const countByKey = {};
		for (const feature of features) {
			const key = feature.properties?.node_uuid;
			if (key != null) countByKey[key] = (countByKey[key] ?? 0) + 1;
		}

		set({ filteredFeatures: features, filteredCountByKey: countByKey });
	},

	// --- Active collection objects (key + label + color, consumed by the map legend) ---
	objects: [],
	setObjects: objects => set({ objects }),

	// --- Hexagon aggregation resolution (m² per cell) ---
	// Feeds the hexagons query key; the results themselves stay in React Query.
	// Seeded from the collection's stored `hexagon.size` and written back on
	// Apply (see useHexagonConfig).
	hexagonResolution: DEFAULT_HEXAGON_RESOLUTION,
	setHexagonResolution: resolution => set({ hexagonResolution: resolution }),

	// --- Hexagon layer color (base hue for the un-classified density heat) ---
	// Picked in Hexagon Settings and persisted alongside the resolution. Only the
	// hue: per-cell opacity stays density-derived, and a classification overrides
	// it. Must stay 6-digit hex — `hexToRgba` returns grey for the 8-digit form.
	hexagonColor: DEFAULT_HEXAGON_COLOR,
	setHexagonColor: color => set({ hexagonColor: color }),

	// --- Hexagon color draft (unsaved, previewed on the map) ---
	// Picked but not yet applied; `null` = no draft, so the map draws
	// `hexagonColor`. Kept separate because that field only ever holds what the
	// collection holds, so it cannot preview. The layer reads this only while
	// the settings panel is open, which stops a preview outliving its panel —
	// the same split `hexagonClassificationFilter` uses. Cell size has no
	// counterpart: previewing it would need a re-aggregation.
	hexagonColorDraft: null,
	setHexagonColorDraft: color => set({ hexagonColorDraft: color }),

	// --- Which collection `hexagonResolution` and `hexagonColor` hold ---
	// `null` = neither seeded yet. Here rather than in the hydrating hook so
	// `reset()` clears it on the same switch that wipes the values it describes,
	// and so a save settling after its panel has gone can tell whether the
	// collection it saved is still on screen. See useHexagonConfig.
	hydratedCollectionUuid: null,
	setHydratedCollectionUuid: uuid => set({ hydratedCollectionUuid: uuid }),

	// --- Selected hexagon cell (for the cell info panel) ---
	// Collection-scoped selection, kept here rather than in Project.context so the
	// collection stack stays self-contained and never feeds the model-oriented
	// hexagon info sidebar a collection-shaped cell.
	//
	// Only the id is stored, not the cell object itself: cells are refetched
	// (new array, new object identities) whenever resolution or filters change,
	// so a stored object would go stale the moment that happens. Consumers
	// re-derive the live cell by looking this id up in the current `cells`
	// array, the same pattern the layer's selection-outline effect already uses.
	selectedHexagonCellId: null,
	setSelectedHexagonCellId: cellId => set({ selectedHexagonCellId: cellId }),

	// --- Active hexagon classification (matrix/simple color-filter) ---
	// null = heat mode (density opacity ramp); a matrix/simple filter config =
	// classified mode (cells colored by class). Owned/edited by the color-filter
	// panel and read by the layer, so coloring previews live as the user edits.
	hexagonClassificationFilter: null,
	setHexagonClassificationFilter: filter => set({ hexagonClassificationFilter: filter }),

	// --- Active DSP classification (matrix/simple color-filter) ---
	// The digital-sampling-plot counterpart to hexagonClassificationFilter, kept
	// as its own field rather than shared: the two layers are mutually exclusive
	// (hexagons are orthophoto-only, plots single-image-only) but their filters
	// reference different property sets, so one config carried across a project
	// switch would classify nothing. null = no classification yet.
	dspClassificationFilter: null,
	setDspClassificationFilter: filter => set({ dspClassificationFilter: filter }),

	// --- Derived selectors (called via getState, not subscribed) ---
	getExpandedKeys: () => Object.keys(get().expandedNodes),
	getCheckedKeys: () => Object.keys(get().checkedNodes),

	// --- Reset ---
	reset: () =>
		set({
			expandedNodes: {},
			checkedNodes: {},
			activeFilterCount: 0,
			selectedObjectId: null,
			filteredFeatures: [],
			filteredCountByKey: {},
			objects: [],
			hexagonResolution: DEFAULT_HEXAGON_RESOLUTION,
			hexagonColor: DEFAULT_HEXAGON_COLOR,
			hexagonColorDraft: null,
			hydratedCollectionUuid: null,
			selectedHexagonCellId: null,
			hexagonClassificationFilter: null,
			dspClassificationFilter: null,
		}),
}));

/**
 * Per-node selector factories.
 * Return a stable boolean so Zustand skips re-renders for unaffected nodes.
 *
 * Note: each call creates a new function reference. Zustand compares the
 * *result* (a boolean), not the selector identity, so this doesn't cause
 * extra re-renders. However, the selector still re-executes on every store
 * update. At scale (500+ nodes), memoize per node:
 *   useMemo(() => selectNodeChecked(node.id), [node.id])
 *
 * Usage:
 *   const isChecked = useCollectionStore(selectNodeChecked(nodeId));
 *   const isExpanded = useCollectionStore(selectNodeExpanded(nodeId));
 */
export const selectNodeChecked = nodeId => state => !!state.checkedNodes[nodeId];
export const selectNodeExpanded = nodeId => state => !!state.expandedNodes[nodeId];
